/**
 * components/landing/ai-platform/sections/AIAcrossPlatform.jsx — §4.8.
 *
 * A single visual proving “AI is in every module”.  Two-column
 * layout: left = list of 9 modules with a short AI-role sentence,
 * right = an SVG showing the AI core beaming lines out to each module.
 */

import React, { useState } from 'react'
import clsx from 'clsx'
import {
  Bot, LineChart, Wand2, FlaskConical, ScanLine, GraduationCap, PieChart, UsersRound, Building2, Sparkles,
} from 'lucide-react'
import { Container, Eyebrow, SectionHeading } from '@/components/landing/design-system'
import { ACROSS } from '../data'
import { TONE } from '../_shared'

const ICONS = { Bot, LineChart, Wand2, FlaskConical, ScanLine, GraduationCap, PieChart, UsersRound, Building2 }

export default function AIAcrossPlatform() {
  const [activeId, setActiveId] = useState(ACROSS[0]?.id)
  return (
    <section className="relative py-24 md:py-28">
      <div className="pointer-events-none absolute inset-0 -z-10 opacity-[0.4]"
        style={{ background: 'radial-gradient(50% 40% at 50% 20%, rgba(255,193,7,0.10), transparent 70%)' }} />
      <Container size="wide" className="flex flex-col gap-12">
        <div className="flex flex-col items-center text-center gap-5 max-w-3xl mx-auto">
          <Eyebrow>AI Across the Platform</Eyebrow>
          <SectionHeading
            align="center"
            title={<>Not a chatbot bolted on. <span className="bg-gradient-to-r from-brand-gold to-[#FFE17A] bg-clip-text text-transparent">A capability woven in.</span></>}
            subtitle="Hover any module to see how AI shows up there. Every capability inherits the same context manager, prompt engine and KB — no duplicated pipelines, no inconsistent behaviour."
          />
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 lg:gap-10 items-start">
          <div className="lg:col-span-5 flex flex-col gap-2">
            {ACROSS.map((m) => {
              const tone = TONE[m.tone] || TONE.orange
              const Icon = ICONS[m.iconKey] || Sparkles
              const active = activeId === m.id
              return (
                <button
                  key={m.id}
                  onMouseEnter={() => setActiveId(m.id)}
                  onFocus={() => setActiveId(m.id)}
                  onClick={() => setActiveId(m.id)}
                  className={clsx(
                    'group text-left rounded-2xl border p-4 transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-gold/60',
                    active
                      ? 'border-white/25 bg-gradient-to-r from-white/[0.05] to-white/[0.01]'
                      : 'border-white/10 bg-white/[0.02] hover:border-white/20',
                  )}
                >
                  <div className="flex items-center gap-3">
                    <span className="inline-flex h-9 w-9 items-center justify-center rounded-lg border"
                      style={{ borderColor: `rgba(${tone.rgb},0.35)`, background: `rgba(${tone.rgb},0.08)`, color: tone.hex }}>
                      <Icon style={{ width: 15, height: 15 }} strokeWidth={1.8} />
                    </span>
                    <div className="flex-1 min-w-0">
                      <div className="text-[14px] font-semibold text-white leading-tight">{m.label}</div>
                      <p className={clsx('text-[12.5px] leading-relaxed mt-0.5', active ? 'text-zinc-200' : 'text-zinc-500 group-hover:text-zinc-300 line-clamp-1')}>
                        {m.body}
                      </p>
                    </div>
                  </div>
                </button>
              )
            })}
          </div>

          <div className="lg:col-span-7">
            <AICoreDiagram activeId={activeId} />
          </div>
        </div>
      </Container>
    </section>
  )
}

function AICoreDiagram({ activeId }) {
  const R = 200, C = 260
  return (
    <div className="aspect-square w-full max-w-[560px] mx-auto">
      <svg viewBox="0 0 520 520" className="w-full h-full">
        <defs>
          <radialGradient id="across-core" cx="50%" cy="50%" r="60%">
            <stop offset="0%"  stopColor="#FBF6E9" stopOpacity="0.95" />
            <stop offset="40%" stopColor="#FFC107" stopOpacity="0.85" />
            <stop offset="100%" stopColor="#7a2f00" stopOpacity="1" />
          </radialGradient>
          <radialGradient id="across-halo" cx="50%" cy="50%" r="60%">
            <stop offset="0%"  stopColor="#FFC107" stopOpacity="0.6" />
            <stop offset="100%" stopColor="#FFC107" stopOpacity="0" />
          </radialGradient>
        </defs>

        {ACROSS.map((m, i) => {
          const a = ((i / ACROSS.length) * 360 - 90) * (Math.PI / 180)
          const x = C + Math.cos(a) * R
          const y = C + Math.sin(a) * R
          const tone = TONE[m.tone] || TONE.orange
          const active = activeId === m.id
          return (
            <line key={`ln-${i}`} x1={C} y1={C} x2={x} y2={y}
              stroke={active ? tone.hex : `rgba(${tone.rgb},0.35)`}
              strokeWidth={active ? 2 : 1}
              strokeDasharray={active ? '0' : '3 6'}
              className={'animate-brand-dash'}
              style={{ opacity: active ? 1 : 0.55, transition: 'opacity 250ms ease' }}
            />
          )
        })}

        <circle cx={C} cy={C} r="110" fill="url(#across-halo)" opacity="0.75" className="animate-brand-pulse" style={{ transformOrigin: `${C}px ${C}px` }} />
        <circle cx={C} cy={C} r="56"  fill="url(#across-core)" />
        <text  x={C} y={C - 2}  textAnchor="middle" fontSize="14" fontWeight="800" fill="#0a0a0a" style={{ letterSpacing: '0.06em' }}>AI</text>
        <text  x={C} y={C + 16} textAnchor="middle" fontSize="9"  fontWeight="700" fill="#0a0a0a" style={{ letterSpacing: '0.28em' }}>CORE</text>

        {ACROSS.map((m, i) => {
          const a = ((i / ACROSS.length) * 360 - 90) * (Math.PI / 180)
          const x = C + Math.cos(a) * R
          const y = C + Math.sin(a) * R
          const tone = TONE[m.tone] || TONE.orange
          const Icon = ICONS[m.iconKey] || Sparkles
          const active = activeId === m.id
          return (
            <g key={`nd-${i}`} transform={`translate(${x} ${y}) scale(${active ? 1.15 : 1})`} style={{ transition: 'transform 250ms ease' }}>
              <circle r="30" fill="rgba(10,10,12,0.9)" stroke={tone.hex} strokeOpacity={active ? 1 : 0.55} strokeWidth={active ? 2 : 1.2} />
              <circle r="27" fill={`${tone.hex}12`} />
              <foreignObject x="-14" y="-14" width="28" height="28">
                <div className="flex items-center justify-center w-full h-full" style={{ color: tone.hex }}>
                  <Icon strokeWidth={1.8} style={{ width: 14, height: 14 }} />
                </div>
              </foreignObject>
              <text y="46" textAnchor="middle" fontSize="10.5" fontWeight={active ? 700 : 600} fill={active ? '#fff' : 'rgba(255,255,255,0.78)'}>{m.label}</text>
            </g>
          )
        })}
      </svg>
    </div>
  )
}
